import React, { useEffect, useState } from 'react';
import API_BASE from '../../utils/api.js';
import notify from '../../utils/notify';
import InvoicePdfDocument from '../Projects/Invoices/InvoicePdfDocument';
import '../../component-styles/InvoiceTemplateSettings.css';

const DEFAULTS = {
  accentColor: '#a5d27d',
  footerText: 'Invoice provided by InteriorFolio',
  termsText: '',
  showRoomColumn: true,
  showItemImages: true,
  defaultEmailSubject: '',
  numberPrefix: 'IN-',
  numberPadding: 5,
};

// Mirrors backend/src/utils/invoiceNumberFormat.js's sanitizeInvoiceNumberFormat
// — keep the two in sync if the rules ever change. Client-side sanitizing here
// is just for instant input feedback; the backend re-sanitizes on every save
// and read, since that's what actually protects the numbering invariant.
const sanitizePrefixInput = (v) =>
  String(v ?? '')
    .replace(/[0-9]/g, '')
    .replace(/[^A-Za-z\s\-_/.#&]/g, '')
    .slice(0, 10);

const formatPreviewNumber = (prefix, padding, n) =>
  `${prefix}${String(n).padStart(padding, '0')}`;

const SAMPLE_INVOICE = {
  invoiceNumber: '00001',
  createdAt: new Date().toISOString(),
  projectName: 'Sample Project',
  client: { name: 'Jane Client' },
  billingAddress: '123 Main St\nAnytown, ST 00000',
  totals: { subtotal: 1450, tax: 116, total: 1566, paid: 500 },
  remainingBalance: 1066,
  lineItems: [
    {
      itemId: 'sample-1',
      name: 'Sofa — Custom Upholstery',
      room: 'Living Room',
      qty: 1,
      clientUnit: 1450,
      lineTotal: 1450,
      unitType: 'Each',
    },
  ],
};

const SAMPLE_COMPANY = { companyName: 'Your Studio Name' };

export default function InvoiceTemplateSettings() {
  const userId = localStorage.getItem('userId');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULTS);
  const [nextNumberSeed, setNextNumberSeed] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API_BASE}/api/settings/${userId}`);
        const data = await res.json();
        setForm({
          accentColor: data.invoiceTemplate?.accentColor || DEFAULTS.accentColor,
          footerText: data.invoiceTemplate?.footerText ?? DEFAULTS.footerText,
          termsText: data.invoiceTemplate?.termsText || DEFAULTS.termsText,
          showRoomColumn: data.invoiceTemplate?.showRoomColumn !== false,
          showItemImages: data.invoiceTemplate?.showItemImages !== false,
          defaultEmailSubject: data.invoiceTemplate?.defaultEmailSubject || DEFAULTS.defaultEmailSubject,
          numberPrefix: data.invoiceTemplate?.numberPrefix ?? DEFAULTS.numberPrefix,
          numberPadding: data.invoiceTemplate?.numberPadding ?? DEFAULTS.numberPadding,
        });
      } catch (err) {
        console.error('Failed to load invoice template settings', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [userId]);

  useEffect(() => {
    async function loadNextNumber() {
      try {
        const res = await fetch(`${API_BASE}/api/invoices/latest?userId=${userId}`);
        const data = await res.json();
        setNextNumberSeed(typeof data?.nextNumber === 'number' ? data.nextNumber : null);
      } catch (err) {
        console.error('Failed to load next invoice number', err);
      }
    }
    loadNextNumber();
  }, [userId]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('idToken');
      const res = await fetch(`${API_BASE}/api/settings/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ invoiceTemplate: form }),
      });
      if (!res.ok) throw new Error('Save failed');
      notify('Invoice template saved!');
    } catch (err) {
      console.error('Save invoice template failed', err);
      notify('Failed to save invoice template.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="it-settings">
      <p className="it-desc">
        Customize how your invoice PDFs look. These settings apply to every invoice you send or download.
      </p>

      <div className="it-grid">
        {/* Left — Controls */}
        <div className="it-controls">
          <div className="form-row">
            <label>Accent Color</label>
            <div className="it-color-row">
              <input
                type="color"
                value={form.accentColor}
                onChange={(e) => set('accentColor', e.target.value)}
                className="it-color-swatch"
              />
              <input
                type="text"
                value={form.accentColor}
                onChange={(e) => set('accentColor', e.target.value)}
                maxLength={7}
                className="it-color-hex"
                placeholder="#a5d27d"
              />
            </div>
          </div>

          <div className="form-row">
            <label>Footer Text</label>
            <input
              type="text"
              value={form.footerText}
              onChange={(e) => set('footerText', e.target.value)}
              placeholder="Invoice provided by InteriorFolio"
            />
          </div>

          <div className="form-row">
            <label>Terms &amp; Conditions</label>
            <textarea
              rows={4}
              value={form.termsText}
              onChange={(e) => set('termsText', e.target.value)}
              placeholder="e.g. Payment due within 30 days. Late payments subject to a 1.5% monthly fee."
              style={{ width: '100%', resize: 'vertical' }}
            />
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
              Optional — shown at the bottom of the invoice, above the footer. Leave blank to omit.
            </p>
          </div>

          <div className="form-row">
            <label>Default Email Subject</label>
            <input
              type="text"
              value={form.defaultEmailSubject}
              onChange={(e) => set('defaultEmailSubject', e.target.value)}
              placeholder='e.g. Invoice {invoiceNumber} from {companyName}'
            />
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
              Use <code>{'{companyName}'}</code> and <code>{'{invoiceNumber}'}</code> as placeholders. Leave blank for the default subject.
            </p>
          </div>

          <div className="form-row">
            <label>Invoice Number Prefix</label>
            <input
              type="text"
              value={form.numberPrefix}
              onChange={(e) => set('numberPrefix', sanitizePrefixInput(e.target.value))}
              maxLength={10}
              placeholder="IN-"
            />
          </div>

          <div className="form-row">
            <label>Digit Padding</label>
            <select
              value={form.numberPadding}
              onChange={(e) => set('numberPadding', Number(e.target.value))}
            >
              {Array.from({ length: 10 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>
                  {d} digit{d > 1 ? 's' : ''} ({formatPreviewNumber('', d, 1)})
                </option>
              ))}
            </select>
            <p className="it-number-preview">
              {nextNumberSeed != null
                ? `Your next invoice will be: ${formatPreviewNumber(form.numberPrefix, form.numberPadding, nextNumberSeed)}`
                : `Example: ${formatPreviewNumber(form.numberPrefix, form.numberPadding, 1)}`}
            </p>
          </div>

          <div className="form-row it-checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={form.showRoomColumn}
                onChange={(e) => set('showRoomColumn', e.target.checked)}
              />
              Show "Room" column on line items
            </label>
          </div>

          <div className="form-row it-checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={form.showItemImages}
                onChange={(e) => set('showItemImages', e.target.checked)}
              />
              Show item images on line items
            </label>
          </div>

          <button className="save-btn" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>

        {/* Right — Live Preview */}
        <div className="it-preview">
          <p className="it-preview-label">Preview</p>
          <div className="it-preview-frame">
            <div className="it-preview-scale">
              <InvoicePdfDocument
                invoice={SAMPLE_INVOICE}
                company={SAMPLE_COMPANY}
                apiBase={API_BASE}
                template={form}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
